/**
 * useTutorSettingsState — les paramètres du tuteur : la page ouverte, les brouillons, l'enregistrement
 * de chaque page, et la sortie gardée.
 *
 * Port de la branche tuteur de `SettingsTabNavigator.js` et de ses onglets, en pages qui s'enregistrent
 * chacune (la règle de l'élève, `DECISIONS.md` 13/10) :
 *
 * - **Enregistrer** valide la page (le schéma de l'ancien formulaire), puis envoie
 *   `authService.updateTutorAccount` avec le corps ENTIER (`toTutorAccountPayload`), les autres pages
 *   avec les valeurs du compte. Au succès, le compte est remplacé par la réponse (réducteur
 *   `currentUser`) et l'accusé s'affiche (« Tes paramètres ont bien été mis à jour », les mots de
 *   l'ancien écran de félicitations, qui n'est plus ouvert). Un refus : les erreurs de champ sous leurs
 *   champs, le message du serveur au-dessus du bouton. Le geste se mesure (`account.settings`).
 * - **La sortie** gardée : `useSettingsSectionShell`, la même que l'élève (`account` si rien n'est
 *   derrière : la feuille de profil).
 * - **Les liens** : « Profil » → `tutor/complete-profile` (l'onglet ne portait que ce bouton),
 *   « Fuseau horaire » → `account-timezone`, « contacte le support » et « Contacter le support »
 *   (`mailto:`), « Modifier mon mot de passe » (`account-password`), « Fermer mon compte »
 *   (`account-close`), « Confidentialité » et « Mentions légales » (navigateur intégré), « Noter
 *   l'application » (mobile seulement).
 *
 * Seul un tuteur arrive ici : le candidat garde l'ancien espace (`selectTutorDsReading`), ses deux
 * onglets n'ont pas de branche.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useReducer, useRef, type Dispatch } from "react"
import { Keyboard } from "react-native"
import { useSelector } from "react-redux"

import { authService } from "../../../../../../data/services/authService"
import {
    toggleNotificationChannel,
    validateAddressSettings,
    type ApiNotificationSettings,
} from "../../../../adapters/student/Account/studentSettingsAdapter"
import {
    toTutorAccountPayload,
    tutorSettingsFieldErrors,
    validateTutorGeneralSettings,
    type ApiTutorSettingsUser,
    type TutorAccountEdits,
    type TutorSettingsSection,
} from "../../../../adapters/tutor/Account/tutorSettingsAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import { type TutorSettingsViewModel } from "../../../../view-models/tutor/Account/tutorSettingsViewModel"
import { type UIStudentAddressSettings } from "../../../types/studentAccount"
import { type UITutorGeneralSettings } from "../../../types/tutorCompte"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { openSupportEmail } from "../../../hooks/openSupportEmail"
import { settingsViewDirty } from "../../../hooks/settingsUiState"
import { useAccountTimezoneEntry } from "../../../hooks/useAccountTimezoneEntry"
import { useApiMutation } from "../../../hooks/useApiMutation"
import {
    LEGAL_URL,
    OTHER_PAGE_REFUSED,
    PRIVACY_URL,
    rateAppAction,
    SETTINGS_SAVED_MESSAGE,
    SETTINGS_SUBMIT_FAILED,
    useSettingsSectionShell,
} from "../../../hooks/useSettingsSectionShell"
import {
    initialTutorSettingsUiState,
    tutorSettingsUiReducer,
    type TutorSettingsUiAction,
    type TutorSettingsUiState,
} from "./tutorSettingsUiState"

type StackNavigation = { push: (route: string, params?: object) => void }

export function useTutorSettingsState(
    vm: TutorSettingsViewModel,
    ui: TutorSettingsUiState,
    dispatchUi: Dispatch<TutorSettingsUiAction>
) {
    const navigation = useNavigation() as unknown as StackNavigation
    const user = useSelector((state: unknown) => selectAccount(state as never) as ApiTutorSettingsUser | undefined)
    const pendingSectionRef = useRef<TutorSettingsSection | null>(null)
    const general = ui.drafts.general ?? vm.general
    const address = ui.drafts.address ?? vm.address

    const { start, requesting } = useApiMutation({
        service: authService.updateTutorAccount,
        gesture: "account.settings",
        surface: "tutor_settings",
        fallback: SETTINGS_SUBMIT_FAILED,
        onSuccess: () => {
            const section = pendingSectionRef.current
            pendingSectionRef.current = null
            if (section) dispatchUi({ type: "SAVED", section, message: SETTINGS_SAVED_MESSAGE })
        },
        onError: (message, error) => {
            const section = pendingSectionRef.current
            pendingSectionRef.current = null
            if (!section) return
            const { fields, elsewhere } = tutorSettingsFieldErrors(error, section)
            dispatchUi({
                type: "INVALID",
                errors: fields,
                submitError: elsewhere ? OTHER_PAGE_REFUSED : message,
            })
        },
    })

    const submit = useCallback(
        (section: TutorSettingsSection) => {
            if (!user || requesting) return
            const errors: Record<string, string> =
                section === "general" && general
                    ? validateTutorGeneralSettings(general)
                    : section === "address" && address
                      ? validateAddressSettings(address)
                      : {}
            if (Object.keys(errors).length > 0) {
                dispatchUi({ type: "INVALID", errors, submitError: null })
                return
            }
            Keyboard.dismiss()
            dispatchUi({ type: "SUBMITTING" })
            pendingSectionRef.current = section
            const edits: TutorAccountEdits =
                section === "general"
                    ? { general: general ?? undefined }
                    : section === "address"
                      ? { address: address ?? undefined }
                      : { notificationSettings: ui.drafts.preferences ?? undefined }
            start({}, toTutorAccountPayload(user, edits))
        },
        [user, requesting, general, address, ui.drafts.preferences, start, dispatchUi]
    )

    const { back, shell } = useSettingsSectionShell({
        ui,
        dispatchUi,
        dirty: settingsViewDirty(ui, vm),
        loading: vm.loading,
        submitting: requesting,
    })

    const preferencesSource: ApiNotificationSettings | null =
        ui.drafts.preferences ?? user?.notification_settings ?? null

    const timezoneEntry = useAccountTimezoneEntry()

    return {
        view: ui.view,
        hub: {
            onBack: back,
            onOpenGeneral: () => dispatchUi({ type: "OPEN", view: "general" }),
            onOpenAddress: () => dispatchUi({ type: "OPEN", view: "address" }),
            onOpenProfile: () => navigation.push("tutor/complete-profile"),
            onOpenPreferences: () => dispatchUi({ type: "OPEN", view: "preferences" }),
            ...timezoneEntry,
            loading: vm.loading,
        },
        general: {
            ...shell,
            values: general,
            errors: ui.errors,
            onChange: (field: keyof UITutorGeneralSettings, value: string) => {
                if (general == null) return
                dispatchUi({ type: "EDIT", section: "general", field, value: { ...general, [field]: value } })
            },
            onContactSupport: openSupportEmail,
            onChangePassword: () => navigation.push("account-password"),
            onSubmit: () => submit("general"),
        },
        address: {
            ...shell,
            values: address,
            errors: ui.errors,
            onChange: (field: keyof UIStudentAddressSettings, value: string) => {
                if (address == null) return
                dispatchUi({ type: "EDIT", section: "address", field, value: { ...address, [field]: value } })
            },
            countries: vm.countries,
            onSubmit: () => submit("address"),
        },
        preferences: {
            ...shell,
            notifications: vm.notifications,
            onToggleNotification: (slug: string, channel: "push" | "email", next: boolean) =>
                dispatchUi({
                    type: "SET_PREFERENCES",
                    value: toggleNotificationChannel(preferencesSource, slug, channel, next),
                }),
            onContactSupport: openSupportEmail,
            onCloseAccount: () => navigation.push("account-close"),
            onOpenPrivacy: () => openExternalUrl(PRIVACY_URL),
            onOpenLegal: () => openExternalUrl(LEGAL_URL),
            onRateApp: rateAppAction(vm.storeUrls),
            onSubmit: () => submit("preferences"),
        },
    }
}

/** L'état de l'écran, tenu par l'écran : son brouillon de notifications nourrit aussi le view-model. */
export function useTutorSettingsUi(initialTab: unknown) {
    return useReducer(tutorSettingsUiReducer, initialTab, initialTutorSettingsUiState)
}
